import { useEffect, useState } from 'react';
import {
  extractPresenceUserCount,
  subscribeToGlobalChannelStream,
  subscribeToScopedStream,
  type StreamEvent,
  type StreamScope,
} from '../lib/sseClient';

type UseChannelStreamOptions = {
  channel?: string;
  scope?: StreamScope;
  identifier?: string | null;
  enabled?: boolean;
  monitorConnection?: boolean;
  notify?: boolean;
  suppressConnectedToast?: boolean;
  suppressPresenceToast?: boolean;
  channelLabel?: string;
  onEvent?: (event: StreamEvent) => void;
};

type UseChannelStreamResult = {
  isConnected: boolean;
  isStopping: boolean;
  connectionError: string | null;
  userCount: number | null;
};

export const useChannelStream = ({
  channel,
  scope,
  identifier,
  enabled = true,
  monitorConnection = false,
  notify = true,
  suppressConnectedToast = true,
  suppressPresenceToast = true,
  channelLabel,
  onEvent,
}: UseChannelStreamOptions): UseChannelStreamResult => {
  const [isConnected, setIsConnected] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [userCount, setUserCount] = useState<number | null>(null);

  useEffect(() => {
    const trimmedChannel = typeof channel === 'string' ? channel.trim() : '';
    const trimmedIdentifier = typeof identifier === 'string' ? identifier.trim() : '';
    const useGlobalChannel = trimmedChannel.length > 0;
    const useScoped = Boolean(scope && trimmedIdentifier);

    if (!enabled || (!useGlobalChannel && !useScoped)) {
      setIsConnected(false);
      setIsStopping(false);
      setConnectionError(null);
      setUserCount(null);
      return;
    }

    const subscription = useGlobalChannel
      ? subscribeToGlobalChannelStream(trimmedChannel, {
          monitorConnection,
          notify,
          suppressConnectedToast,
          suppressPresenceToast,
          channelLabel,
          onOpen: () => {
            setIsStopping(false);
            setIsConnected(true);
            setConnectionError(null);
          },
          onError: () => {
            setIsConnected(false);
            setConnectionError('Live stream connection failed');
          },
          onEvent: (event) => {
            if (event.name === 'server.stopping') setIsStopping(true);
            if (event.name === 'connected') {
              setIsConnected(true);
              setConnectionError(null);
            }
            if (event.name === 'presence.updated') {
              setUserCount(extractPresenceUserCount(event.payload));
            }
            onEvent?.(event);
          },
        })
      : subscribeToScopedStream(scope as StreamScope, trimmedIdentifier, {
          monitorConnection,
          notify,
          suppressConnectedToast,
          suppressPresenceToast,
          channelLabel,
          onOpen: () => {
            setIsStopping(false);
            setIsConnected(true);
            setConnectionError(null);
          },
          onError: () => {
            setIsConnected(false);
            setConnectionError('Live stream connection failed');
          },
          onEvent: (event) => {
            if (event.name === 'server.stopping') setIsStopping(true);
            if (event.name === 'connected') {
              setIsConnected(true);
              setConnectionError(null);
            }
            if (event.name === 'presence.updated') {
              setUserCount(extractPresenceUserCount(event.payload));
            }
            onEvent?.(event);
          },
        });

    return () => {
      subscription.close();
      setIsConnected(false);
    };
  }, [
    channel,
    channelLabel,
    enabled,
    monitorConnection,
    identifier,
    notify,
    onEvent,
    scope,
    suppressConnectedToast,
    suppressPresenceToast,
  ]);

  return {
    isConnected,
    isStopping,
    connectionError,
    userCount,
  };
};
